<template>
	<transition name="modal-fade">
		<div v-if="isModalVisible" class="modal-backdrop">
			<div
				class="modal"
				role="dialog"
				aria-labelledby="globalAddModalTitle"
				aria-describedby="globalAddModalDescription"
			>
				<header class="modal-header" id="globalAddModalTitle">
					<div class="modal-header-content">
						<div class="modal-title-section">
							<h2 class="modal-main-title">Add Special Price</h2>
						</div>
						<div class="modal-subtitle">
							Select a customer and stock item to add a special price
						</div>
					</div>
				</header>

				<!-- Close Button -->
				<button
					class="modal-close-absolute"
					@click="closeModal"
					aria-label="Close modal"
				>
					<span class="close-x">✕</span>
					<span class="close-text">Close</span>
				</button>

				<section class="modal-body" id="globalAddModalDescription">
					<!-- Error Message Display -->
					<b-message
						v-if="errorMessage"
						type="is-danger"
						:closable="true"
						@close="errorMessage = null"
					>
						{{ errorMessage }}
					</b-message>

					<!-- Success Message Display -->
					<b-message
						v-if="successMessage"
						type="is-success"
						:closable="true"
						@close="successMessage = null"
					>
						{{ successMessage }}
					</b-message>

					<div class="form-container">
						<!-- Customer Selection -->
						<div class="form-group">
							<label class="form-label">Select Customer</label>
							<b-field>
								<b-autocomplete
									v-model="customerSearchText"
									:data="filteredCustomers"
									placeholder="Search and select customer..."
									field="displayText"
									:loading="customersLoading"
									@select="onCustomerSelectedFromAutocomplete"
									:clearable="true"
									:open-on-focus="true"
									expanded
								>
									<template slot="empty">
										<span class="no-results">No matching customers found</span>
									</template>
									<template slot-scope="props">
										<div class="customer-option">
											<span class="customer-id">{{
												props.option.customer_id
											}}</span>
											<span class="separator">-</span>
											<span class="customer-name">{{
												props.option.customer_title
											}}</span>
											<span
												v-if="props.option.customer_nickname"
												class="customer-nickname"
											>
												({{ props.option.customer_nickname }})
											</span>
										</div>
									</template>
								</b-autocomplete>
							</b-field>
						</div>

						<!-- Stock Selection -->
						<div class="form-group">
							<label class="form-label">Select Stock Item</label>
							<b-field>
								<b-autocomplete
									v-model="stockSearchText"
									:data="filteredStocks"
									placeholder="Search and select stock item..."
									field="displayText"
									:loading="stocksLoading"
									:disabled="!selectedCustomerId"
									@select="onStockSelectedFromAutocomplete"
									:clearable="true"
									:open-on-focus="true"
									expanded
								>
									<template slot="empty">
										<span class="no-results">No matching stocks found</span>
									</template>
									<template slot-scope="props">
										<div class="stock-option">
											<span class="stock-id">{{ props.option.stock_id }}</span>
											<span class="stock-separator">-</span>
											<span class="stock-name">{{
												props.option.stock_name
											}}</span>
											<span class="stock-separator">-</span>
											<span class="stock-price">{{
												formatPriceValue(props.option.customerPrice)
											}}</span>
										</div>
									</template>
								</b-autocomplete>
							</b-field>
							<p
								v-if="
									!hasAvailableStocks && noStocksMessage && selectedCustomerId
								"
								class="help is-danger"
							>
								{{ noStocksMessage }}
							</p>
						</div>

						<!-- Current Price Display -->
						<div class="form-group">
							<label class="form-label">Current Customer Class Price</label>
							<div class="current-price-display">
								<span v-if="selectedStock" class="price-label"
									>Class {{ customerPriceClass }}:</span
								>
								{{
									selectedStock
										? formatPriceValue(selectedStock.customerPrice)
										: '-'
								}}
							</div>
						</div>

						<!-- Special Price Input -->
						<div class="form-group">
							<label class="form-label">Special Price</label>
							<b-field>
								<b-input
									v-model="specialPrice"
									type="number"
									placeholder="Enter special price"
									min="0"
									step="1"
									:has-counter="false"
									:disabled="!selectedStock"
									expanded
								/>
							</b-field>
						</div>

						<!-- Action Buttons -->
						<div class="form-actions">
							<button
								class="btn-save"
								@click="saveSpecialPrice"
								:disabled="
									isSaving ||
									!selectedCustomer ||
									!selectedStock ||
									!isValidPrice
								"
							>
								<svg
									v-if="!isSaving"
									xmlns="http://www.w3.org/2000/svg"
									width="16"
									height="16"
									viewBox="0 0 24 24"
									fill="none"
									stroke="currentColor"
									stroke-width="2"
									stroke-linecap="round"
									stroke-linejoin="round"
								>
									<polyline points="20 6 9 17 4 12"></polyline>
								</svg>
								<span v-if="!isSaving">Save Special Price</span>
								<span v-else>Saving...</span>
							</button>
							<button class="btn-cancel" @click="resetForm">
								<svg
									xmlns="http://www.w3.org/2000/svg"
									width="16"
									height="16"
									viewBox="0 0 24 24"
									fill="none"
									stroke="currentColor"
									stroke-width="2"
									stroke-linecap="round"
									stroke-linejoin="round"
								>
									<line x1="18" y1="6" x2="6" y2="18"></line>
									<line x1="6" y1="6" x2="18" y2="18"></line>
								</svg>
								<span>Clear</span>
							</button>
						</div>
					</div>
				</section>
			</div>
		</div>
	</transition>
</template>

<script>
import { ref, watch, computed } from '@vue/composition-api'
import { useActiveCustomers } from '../composables/useActiveCustomers'
import { useStockItemsWithCustomerPrice } from '../composables/useStockItemsWithCustomerPrice'

export default {
	name: 'GlobalAddSpecialPriceModal',
	props: {
		visible: {
			type: Boolean,
			default: false,
		},
		companyId: {
			type: String,
			required: true,
		},
	},
	setup(props, { emit }) {
		const isModalVisible = ref(false)
		const selectedCustomerId = ref(null)
		const selectedCustomer = ref(null)
		const selectedStock = ref(null)
		const specialPrice = ref('')
		const isSaving = ref(false)
		const errorMessage = ref(null)
		const successMessage = ref(null)
		const customerSearchText = ref('')
		const stockSearchText = ref('')

		// Use the composable to get active customers
		const {
			formattedCustomerOptions,
			loading: customersLoading,
			error: customersError,
			getCustomerById,
			refreshCustomers,
		} = useActiveCustomers(props.companyId)

		// Use the composable to get stock items with customer prices
		// Initialize once with reactive customer ID - it will react to changes
		const {
			formattedStockOptions: stockOptions,
			loading: stocksLoading,
			error: stocksError,
			formatPrice,
			hasAvailableStocks,
			noStocksMessage,
			customerPriceClass,
		} = useStockItemsWithCustomerPrice(
			props.companyId,
			computed(() => selectedCustomerId.value),
			ref([]), // No existing prices to filter in global add
		)

		// Watch for visibility changes
		watch(
			() => props.visible,
			(newVal) => {
				isModalVisible.value = newVal
				if (newVal) {
					// Refresh customers when modal opens
					refreshCustomers()
				}
			},
		)

		// Watch for customer loading errors
		watch(customersError, (error) => {
			if (error) {
				errorMessage.value = 'Failed to load customers. Please try again.'
				console.error('Error loading customers:', error)
			}
		})

		// Computed property to validate price
		const isValidPrice = computed(() => {
			const price = parseInt(specialPrice.value)
			return !isNaN(price) && price >= 0
		})

		// Computed property for filtered customers based on search text
		const filteredCustomers = computed(() => {
			if (!customerSearchText.value) {
				return formattedCustomerOptions.value
			}

			const searchTerm = customerSearchText.value.toLowerCase()
			return formattedCustomerOptions.value.filter((customer) => {
				return (
					customer.customer_id.toLowerCase().includes(searchTerm) ||
					customer.customer_title.toLowerCase().includes(searchTerm) ||
					(customer.customer_nickname &&
						customer.customer_nickname.toLowerCase().includes(searchTerm))
				)
			})
		})

		// Handle customer selection from autocomplete
		const onCustomerSelectedFromAutocomplete = (option) => {
			if (option) {
				selectedCustomerId.value = option.customer_id
				onCustomerSelected(option.customer_id)
			}
		}

		// Computed property for filtered stocks based on search text
		const filteredStocks = computed(() => {
			const stocks = stockOptions.value || []
			if (!stockSearchText.value) {
				return stocks
			}

			const searchTerm = stockSearchText.value.toLowerCase()
			return stocks.filter((stock) => {
				return (
					stock.stock_id.toLowerCase().includes(searchTerm) ||
					stock.stock_name.toLowerCase().includes(searchTerm) ||
					stock.displayText.toLowerCase().includes(searchTerm)
				)
			})
		})

		// Handle stock selection from autocomplete
		const onStockSelectedFromAutocomplete = (option) => {
			if (option) {
				selectedStock.value = option
				specialPrice.value = ''
				errorMessage.value = null
			}
		}

		// Handle customer selection
		const onCustomerSelected = async (customerId) => {
			selectedCustomer.value = getCustomerById(customerId)
			// Reset stock selection
			selectedStock.value = null
			specialPrice.value = ''
			stockSearchText.value = ''
			errorMessage.value = null
		}

		// Save special price
		const saveSpecialPrice = async () => {
			if (
				!isValidPrice.value ||
				!selectedStock.value ||
				!selectedCustomer.value
			) {
				errorMessage.value = 'Please fill all required fields'
				return
			}

			isSaving.value = true
			errorMessage.value = null

			try {
				const priceData = {
					customer_id: selectedCustomer.value.customer_id,
					stock_id: selectedStock.value.stock_id,
					stock_name: selectedStock.value.stock_name,
					customers_price: parseInt(specialPrice.value),
				}

				// Emit event to parent to handle the save
				emit('save-price', priceData)

				// Show success and reset
				successMessage.value = 'Special price added successfully'
				setTimeout(() => {
					resetForm()
					successMessage.value = null
				}, 1500)
			} catch (error) {
				errorMessage.value = 'Failed to save special price. Please try again.'
				console.error('Error saving special price:', error)
			} finally {
				isSaving.value = false
			}
		}

		// Reset form
		const resetForm = () => {
			selectedCustomerId.value = null
			selectedCustomer.value = null
			selectedStock.value = null
			specialPrice.value = ''
			customerSearchText.value = ''
			stockSearchText.value = ''
			errorMessage.value = null
		}

		// Close modal
		const closeModal = () => {
			resetForm()
			successMessage.value = null
			emit('close')
		}

		const formatPriceValue = (price) => {
			// Use the formatPrice function from the composable
			if (formatPrice) {
				return formatPrice(price)
			}
			// Fallback formatting
			if (!price && price !== 0) return '0.00'
			return new Intl.NumberFormat('sv-SE', {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2,
			}).format(price / 100)
		}

		// Watch for stock loading errors
		watch(stocksError, (error) => {
			if (error && selectedCustomerId.value) {
				// Only show error if a customer is actually selected
				errorMessage.value = 'Failed to load stock items. Please try again.'
				console.error('Error loading stock items:', error)
			}
		})

		return {
			isModalVisible,
			selectedCustomerId,
			selectedCustomer,
			selectedStock,
			specialPrice,
			isSaving,
			errorMessage,
			successMessage,
			customersLoading,
			formattedCustomerOptions,
			stocksLoading,
			stockOptions,
			customerPriceClass,
			hasAvailableStocks,
			noStocksMessage,
			isValidPrice,
			formatPriceValue,
			customerSearchText,
			stockSearchText,
			filteredCustomers,
			filteredStocks,
			onCustomerSelectedFromAutocomplete,
			onStockSelectedFromAutocomplete,
			onCustomerSelected,
			saveSpecialPrice,
			resetForm,
			closeModal,
		}
	},
}
</script>

<style lang="scss" scoped>
// Import typography variables
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

$font-family-base: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI',
	Roboto, 'Helvetica Neue', Arial, sans-serif;
$font-weight-normal: 400;
$font-weight-medium: 500;
$font-weight-semibold: 600;
$font-weight-bold: 700;

.modal-backdrop {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background-color: rgba(0, 0, 0, 0.75);
	backdrop-filter: blur(4px);
	display: flex;
	justify-content: center;
	align-items: center;
	z-index: 11000;
	padding: 20px;
}

.modal {
	position: relative;
	background: white;
	box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
	overflow: hidden;
	display: flex;
	flex-direction: column;
	max-width: 900px;
	width: 900px;
	max-height: 85vh;
	border-radius: 12px;
	margin: auto;
	font-family: $font-family-base;
}

.modal-header {
	background: linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%);
	color: white;
	padding: 28px 35px;
	margin-top: 30px;
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	border-bottom: none;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
	position: relative;
	flex-shrink: 0;
	border-radius: 12px 12px 0 0;

	.modal-header-content {
		flex: 1;
		display: flex;
		flex-direction: column;
		gap: 8px;
	}

	.modal-title-section {
		.modal-main-title {
			font-size: 1.5rem;
			font-weight: $font-weight-bold;
			margin: 0;
			color: white;
			letter-spacing: -0.025em;
			line-height: 1.2;
		}
	}

	.modal-subtitle {
		font-size: 0.875rem;
		color: rgba(255, 255, 255, 0.9);
		line-height: 1.5;
		font-weight: $font-weight-normal;
	}
}

.modal-close-absolute {
	position: absolute;
	top: 30px;
	right: 30px;
	background: #dc3545;
	color: white;
	border: none;
	border-radius: 6px;
	padding: 10px 20px;
	font-family: $font-family-base;
	font-size: 0.9375rem;
	font-weight: $font-weight-semibold;
	cursor: pointer;
	display: flex;
	align-items: center;
	gap: 8px;
	z-index: 10;
	transition: all 0.2s ease;
	box-shadow: 0 4px 12px rgba(220, 53, 69, 0.25);

	&:hover {
		background: #c82333;
		transform: translateY(-2px);
		box-shadow: 0 6px 16px rgba(220, 53, 69, 0.35);
	}
}

.modal-body {
	padding: 30px 35px;
	overflow-y: auto;
	flex-grow: 1;
	background: linear-gradient(to bottom, #ffffff 0%, #f8f9fa 100%);

	.message {
		margin-bottom: 20px;
		border-radius: 8px;
	}
}

.form-container {
	.form-group {
		margin-bottom: 24px;

		.form-label {
			display: block;
			margin-bottom: 8px;
			font-size: 0.9375rem;
			font-weight: $font-weight-semibold;
			color: #495057;
			letter-spacing: -0.01em;
		}

		.current-price-display {
			padding: 12px 16px;
			background: #e9ecef;
			border-radius: 6px;
			font-size: 1.125rem;
			font-weight: $font-weight-semibold;
			color: #2d5a87;
			font-variant-numeric: tabular-nums;
			display: flex;
			align-items: center;
			gap: 12px;

			.price-label {
				font-size: 0.875rem;
				color: #6c757d;
				font-weight: $font-weight-medium;
				background: rgba(255, 255, 255, 0.7);
				padding: 2px 8px;
				border-radius: 4px;
			}
		}
	}

	.form-actions {
		display: flex;
		gap: 12px;
		margin-top: 32px;
		padding-top: 24px;
		border-top: 1px solid #dee2e6;

		.btn-save,
		.btn-cancel {
			display: inline-flex;
			align-items: center;
			gap: 8px;
			padding: 12px 24px;
			border: none;
			border-radius: 6px;
			font-family: $font-family-base;
			font-size: 0.9375rem;
			font-weight: $font-weight-semibold;
			cursor: pointer;
			transition: all 0.2s ease;

			svg {
				width: 16px;
				height: 16px;
			}

			&:disabled {
				opacity: 0.5;
				cursor: not-allowed;
			}
		}

		.btn-save {
			background: linear-gradient(135deg, #48bb78 0%, #38a169 100%);
			color: white;

			&:hover:not(:disabled) {
				transform: translateY(-2px);
				box-shadow: 0 4px 12px rgba(72, 187, 120, 0.4);
			}
		}

		.btn-cancel {
			background: white;
			color: #6c757d;
			border: 1px solid #dee2e6;

			&:hover {
				background: #f8f9fa;
				transform: translateY(-1px);
			}
		}
	}
}

// Buefy autocomplete dropdown width and styling overrides
::v-deep .autocomplete {
	.dropdown-menu {
		min-width: 600px !important;
		width: auto !important;
	}

	.dropdown-content {
		max-width: none !important;
		min-width: 600px !important;
		width: auto !important;
	}

	.dropdown-item {
		white-space: nowrap !important;
		overflow: visible !important;
		text-overflow: initial !important;
	}
}

// Additional dropdown positioning
::v-deep .dropdown .dropdown-menu {
	min-width: 600px !important;
}

// Autocomplete option styling
.customer-option,
.stock-option {
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 8px 12px;
	white-space: nowrap;
	min-width: 580px;

	.customer-id,
	.stock-id {
		font-family: 'SF Mono', Monaco, 'Courier New', monospace;
		font-size: 0.875rem;
		color: #6c757d;
		font-weight: $font-weight-medium;
	}

	.separator,
	.stock-separator {
		color: #dee2e6;
	}

	.customer-name,
	.stock-name {
		flex: 1;
		color: #212529;
		min-width: 200px;
	}

	.customer-nickname {
		font-style: normal;
		color: #6c757d;
		font-size: 0.875rem;
	}

	.stock-price {
		font-weight: $font-weight-semibold;
		color: #2d5a87;
		font-variant-numeric: tabular-nums;
		margin-left: auto;
		padding-left: 16px;
	}
}

.no-results {
	display: block;
	padding: 8px 12px;
	color: #6c757d;
	font-style: italic;
}

.modal-fade-enter,
.modal-fade-leave-to {
	opacity: 0;
	transform: scale(0.95);
}

.modal-fade-enter-active,
.modal-fade-leave-active {
	transition: all 0.3s ease;
}

.modal-fade-enter-to,
.modal-fade-leave {
	opacity: 1;
	transform: scale(1);
}
</style>
